import { Activity, Users, FileText, MessageSquare, TrendingUp, TrendingDown, Minus } from 'lucide-react'

interface EngagementMetricsProps {
  data: {
    engagement_score: number
    active_members: number
    active_patients: number
    reports_30d: number
    messages_30d?: number
    sessions_30d?: number
    avg_session_duration?: number
    last_activity_at?: string | null
  }
  loading?: boolean
}

export function EngagementMetrics({ data, loading = false }: EngagementMetricsProps) {
  const getScoreColor = (score: number) => {
    if (score >= 70) return 'text-green-600'
    if (score >= 40) return 'text-amber-600'
    return 'text-red-600'
  }

  const getScoreBg = (score: number) => {
    if (score >= 70) return 'bg-green-100'
    if (score >= 40) return 'bg-amber-100'
    return 'bg-red-100'
  }

  const getScoreLabel = (score: number) => {
    if (score >= 70) return 'Alto'
    if (score >= 40) return 'Médio'
    return 'Baixo'
  }

  const getScoreIcon = (score: number) => {
    if (score >= 70) return TrendingUp
    if (score >= 40) return Minus
    return TrendingDown
  }

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '-'
    if (seconds < 60) return `${seconds}s`
    if (seconds < 3600) return `${Math.floor(seconds / 60)}min`
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}min`
  }

  const formatLastActivity = (dateStr?: string | null) => {
    if (!dateStr) return 'Nunca'
    const date = new Date(dateStr)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

    if (diffHours < 1) return 'Há poucos minutos'
    if (diffHours < 24) return `Há ${diffHours}h`
    if (diffDays === 1) return 'Ontem'
    if (diffDays < 7) return `Há ${diffDays} dias`
    if (diffDays < 30) return `Há ${Math.floor(diffDays / 7)} semanas`
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="h-6 bg-gray-200 rounded w-48 mb-6 animate-pulse" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="space-y-2">
              <div className="h-4 bg-gray-100 rounded w-20 animate-pulse" />
              <div className="h-8 bg-gray-100 rounded w-16 animate-pulse" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  const ScoreIcon = getScoreIcon(data.engagement_score)

  const metrics = [
    {
      label: 'Membros Ativos',
      value: data.active_members,
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
    },
    {
      label: 'Pacientes Ativos',
      value: data.active_patients,
      icon: Users,
      color: 'text-purple-600',
      bgColor: 'bg-purple-100',
    },
    {
      label: 'Relatórios (30d)',
      value: data.reports_30d,
      icon: FileText,
      color: 'text-teal-600',
      bgColor: 'bg-teal-100',
    },
    {
      label: 'Mensagens (30d)',
      value: data.messages_30d ?? '-',
      icon: MessageSquare,
      color: 'text-amber-600',
      bgColor: 'bg-amber-100',
    },
  ]

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Activity className="w-5 h-5 text-gray-500" />
          Métricas de Engajamento
        </h3>
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${getScoreBg(data.engagement_score)}`}>
          <ScoreIcon className={`w-4 h-4 ${getScoreColor(data.engagement_score)}`} />
          <span className={`text-sm font-semibold ${getScoreColor(data.engagement_score)}`}>
            {data.engagement_score}% - {getScoreLabel(data.engagement_score)}
          </span>
        </div>
      </div>

      {/* Engagement Score Bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-500">Score de Engajamento</span>
          <span className={`text-lg font-bold ${getScoreColor(data.engagement_score)}`}>
            {data.engagement_score}%
          </span>
        </div>
        <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${
              data.engagement_score >= 70
                ? 'bg-green-500'
                : data.engagement_score >= 40
                  ? 'bg-amber-500'
                  : 'bg-red-500'
            }`}
            style={{ width: `${data.engagement_score}%` }}
          />
        </div>
        <div className="flex justify-between mt-1 text-xs text-gray-400">
          <span>0%</span>
          <span>40%</span>
          <span>70%</span>
          <span>100%</span>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {metrics.map((metric) => (
          <div key={metric.label} className="p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2 mb-1">
              <div className={`w-6 h-6 ${metric.bgColor} rounded flex items-center justify-center`}>
                <metric.icon className={`w-3.5 h-3.5 ${metric.color}`} />
              </div>
              <span className="text-xs text-gray-500">{metric.label}</span>
            </div>
            <p className="text-xl font-semibold text-gray-900">{metric.value}</p>
          </div>
        ))}
      </div>

      {/* Additional Stats */}
      <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 gap-4">
        <div>
          <span className="text-xs text-gray-500">Última Atividade</span>
          <p className="text-sm font-medium text-gray-900">{formatLastActivity(data.last_activity_at)}</p>
        </div>
        {data.avg_session_duration !== undefined && (
          <div>
            <span className="text-xs text-gray-500">Duração Média da Sessão</span>
            <p className="text-sm font-medium text-gray-900">{formatDuration(data.avg_session_duration)}</p>
          </div>
        )}
      </div>
    </div>
  )
}
